import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Spinner, Card } from 'react-bootstrap';

import AutocompleteSelect from '../../../../cabinet/wizplain/components/Autocomplete/AutocompleteSelect';
import FileUploader from '../../../../../components/FileUpload/FileUploader';
import { createNewDoc_KG, uploadFile } from '../DocumentsList/actions';

import {
  getGroupChildrenInfo,
  searchKindergartens,
  getKindergarten,
  transferChildrenToAnotherKindergarten
} from './actions';

const placeTypeOptions = [
  { value: 'standart', label: 'Повного дня' },
  { value: 'without-bed', label: 'Короткотривалого перебування' },
  { value: 'inclusive', label: 'Інклюзивне повного дня' },
  { value: 'inclusive-short', label: 'Інклюзивне короткотривалого перебування' }
];

const ERROR_MESSAGES = {
  4001: 'Відсутні обов\'язкові параметри запиту.',
  4042: 'Не вдається знайти вказану групу.',
  4067: 'Не вдалося знайти всіх обраних дітей у групі. Оновіть сторінку та спробуйте ще раз.',
};

const getErrorMessage = (error) => {
  if (!error) return 'Виникла помилка.';
  return ERROR_MESSAGES[error.code] || error.message || 'Виникла помилка.';
};

const formatAgeRange = (ageLimit) => {
  if (!ageLimit) return '';
  const { from, to } = ageLimit;
  if (from === undefined && to === undefined) return '';
  if (from !== undefined && to !== undefined) return `${from}-${to} років`;
  if (from !== undefined) return `від ${from} років`;
  return `до ${to} років`;
};

const formatGroupLabel = (g) => {
  const details = [g.type, formatAgeRange(g.ageLimit), g.periodStartYear ? `${g.periodStartYear}-${Number(g.periodStartYear) + 1} н.р.` : null]
    .filter(Boolean)
    .join(', ');
  return `№${g.number} - ${g.name}${details ? ` (${details})` : ''}`;
};

const formatBirthDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = d.getUTCFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

const base64ToArrayBuffer = (base64) => {
  const binaryString = atob(base64.split(',')[1]);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

const getPlaceTypeLabel = (value) => {
  const found = placeTypeOptions.find(t => t.value === value);
  return found ? found.label : (value || '—');
};

const getCurrentDate = () => new Date().toISOString().split('T')[0];

/**
 * Modal: admin bulk-transfers selected children from the current group to a
 * group in another kindergarten. Permanent — frees the place here, one
 * shared order (number/date/scan) covers the whole batch. Every child gets
 * a full status-history entry (visible via /v1/children/:id/statuses).
 *
 * Props:
 *  - show, onHide
 *  - kgId       - source kindergarten id
 *  - sourceGroup - the source group object ({ _id, number, name, ... })
 *  - onSuccess(childrenIds) - called after a successful transfer
 */
const TransferToAnotherKindergartenModal = ({ show, onHide, kgId, sourceGroup, onSuccess }) => {
  const [step, setStep] = useState('form'); // form | processing | complete
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [childrenList, setChildrenList] = useState([]);
  const [selected, setSelected] = useState({});

  const [targetKindergarten, setTargetKindergarten] = useState('');
  const [availableKindergartens, setAvailableKindergartens] = useState([]);
  const [searchingKindergartens, setSearchingKindergartens] = useState(false);

  const [availableGroups, setAvailableGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [targetGroup, setTargetGroup] = useState('');

  const [placeType, setPlaceType] = useState('');
  const [transferDate, setTransferDate] = useState(getCurrentDate());
  const [orderNumber, setOrderNumber] = useState('');
  const [orderDate, setOrderDate] = useState('');
  const [reason, setReason] = useState('');
  const [files, setFiles] = useState([]);

  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [transferredCount, setTransferredCount] = useState(0);

  const resetForm = useCallback(() => {
    setStep('form');
    setChildrenList([]);
    setSelected({});
    setTargetKindergarten('');
    setAvailableKindergartens([]);
    setAvailableGroups([]);
    setTargetGroup('');
    setPlaceType('');
    setTransferDate(getCurrentDate());
    setOrderNumber('');
    setOrderDate('');
    setReason('');
    setFiles([]);
    setErrors({});
    setSubmitError('');
    setTransferredCount(0);
  }, []);

  const loadChildren = useCallback(async () => {
    if (!kgId || !sourceGroup) return;
    setLoadingChildren(true);
    try {
      const response = await getGroupChildrenInfo(kgId, sourceGroup._id);
      const list = (response && response.data) || [];
      setChildrenList(
        list
          .filter(ch => ch.firstName && !ch.isReserv)
          .sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''))
      );
    } catch (e) {
      console.error('Error loading children:', e);
      setSubmitError('Не вдалося завантажити список дітей групи.');
    } finally {
      setLoadingChildren(false);
    }
  }, [kgId, sourceGroup]);

  const loadInitialKindergartens = useCallback(async () => {
    try {
      setSearchingKindergartens(true);
      const response = await searchKindergartens('', 10);
      // Не показуємо поточний садочок — переводити "в себе" не має сенсу.
      if (response.data) setAvailableKindergartens(response.data.filter(kg => kg._id !== kgId));
    } catch (e) {
      console.error('Error loading kindergartens:', e);
    } finally {
      setSearchingKindergartens(false);
    }
  }, [kgId]);

  useEffect(() => {
    if (show) {
      resetForm();
      loadChildren();
      loadInitialKindergartens();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, sourceGroup]);

  const handleKindergartenSearch = async (search) => {
    if (!search) return;
    try {
      setSearchingKindergartens(true);
      const response = await searchKindergartens(search, 20);
      // Не показуємо поточний садочок — переводити "в себе" не має сенсу.
      if (response.data) setAvailableKindergartens(response.data.filter(kg => kg._id !== kgId));
    } catch (e) {
      console.error('Error searching kindergartens:', e);
    } finally {
      setSearchingKindergartens(false);
    }
  };

  const handleTargetKindergartenChange = async (value) => {
    setTargetKindergarten(value);
    setTargetGroup('');
    setAvailableGroups([]);
    if (errors.targetKindergarten) setErrors(prev => ({ ...prev, targetKindergarten: '' }));

    if (!value) return;
    try {
      setLoadingGroups(true);
      const response = await getKindergarten(value);
      const groups = (response.data && response.data[0] && response.data[0].groups) || [];
      setAvailableGroups(
        [...groups].sort((a, b) => {
          if (`${a.periodStartYear}` !== `${b.periodStartYear}`) {
            return `${b.periodStartYear}`.localeCompare(`${a.periodStartYear}`);
          }
          return `${a.number}`.localeCompare(`${b.number}`, undefined, { numeric: true });
        })
      );
    } catch (e) {
      console.error('Error loading target kindergarten groups:', e);
      setSubmitError('Помилка завантаження списку груп для обраного садочка.');
    } finally {
      setLoadingGroups(false);
    }
  };

  const toggleChild = (childId) => {
    setSelected(prev => {
      const next = { ...prev };
      if (next[childId]) delete next[childId];
      else next[childId] = true;
      return next;
    });
  };

  const selectAll = () => {
    const next = {};
    childrenList.forEach(ch => { next[ch._id] = true; });
    setSelected(next);
  };
  const deselectAll = () => setSelected({});
  const selectedCount = Object.keys(selected).length;

  const selectedTargetGroupObj = useMemo(
    () => availableGroups.find(g => g._id === targetGroup),
    [availableGroups, targetGroup]
  );

  const validateForm = () => {
    const newErrors = {};
    if (!selectedCount) newErrors.children = 'Оберіть хоча б одну дитину для переведення.';
    if (!targetKindergarten) newErrors.targetKindergarten = 'Оберіть садочок для переведення.';
    else if (targetKindergarten === kgId) newErrors.targetKindergarten = 'Цільовий садочок повинен відрізнятись від поточного.';
    if (!targetGroup) newErrors.targetGroup = 'Оберіть групу для переведення.';
    if (!placeType) newErrors.placeType = 'Оберіть тип місця.';
    if (!transferDate) newErrors.transferDate = 'Вкажіть дату переведення.';
    if (!orderNumber || !orderNumber.trim()) newErrors.orderNumber = 'Номер наказу обов\'язковий.';
    if (!orderDate) newErrors.orderDate = 'Дата наказу обов\'язкова.';
    if (!reason || !reason.trim()) newErrors.reason = 'Причина переведення обов\'язкова.';
    if (!files || files.length === 0) newErrors.files = 'Скан-копія наказу обов\'язкова.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!validateForm()) return;

    const childrenIds = Object.keys(selected);
    const confirmationMessage = `Ви переводите ${childrenIds.length} дитин${childrenIds.length === 1 ? 'у' : ''} до садочка "${availableKindergartens.find(kg => kg._id === targetKindergarten)?.name || ''}", група №${selectedTargetGroupObj?.number}, з ${new Date(transferDate).toLocaleDateString('uk-UA')}. Місця у поточній групі буде звільнено. Продовжити?`;
    if (!window.confirm(confirmationMessage)) return;

    setStep('processing');
    try {
      // 1. Create the order document against the source kindergarten.
      const documentName = `Наказ №${orderNumber} - Масове переведення до іншого садочка`;
      const createdDocument = await createNewDoc_KG({
        target_id: kgId,
        type: 'transfer_order',
        name: documentName,
        description: `Наказ на масове переведення дітей до іншого садочку. ${reason}`
      });
      if (!createdDocument || !createdDocument.data || !createdDocument.data._id) {
        throw new Error('Не вдалося створити документ переведення');
      }
      const documentId = createdDocument.data._id;

      // 2. Upload the scan(s).
      await Promise.all(files.map(file => {
        const fileInfo = {
          doc_id: documentId,
          name: file.name,
          mtype: file.type,
          length: file.size,
          date: new Date().toISOString()
        };
        const fileBuffer = base64ToArrayBuffer(file.file);
        return uploadFile(fileInfo, fileBuffer);
      }));

      // 3. Do the actual transfer.
      const response = await transferChildrenToAnotherKindergarten(
        kgId,
        sourceGroup.number,
        targetKindergarten,
        selectedTargetGroupObj.number,
        {
          childrenIds,
          placeType,
          transferDate,
          orderNumber,
          orderDate,
          reason,
          documentId
        }
      );

      if (response && response.error) {
        setSubmitError(getErrorMessage(response.error));
        setStep('form');
        return;
      }

      setTransferredCount(childrenIds.length);
      setChildrenList(prev => prev.filter(ch => !childrenIds.includes(ch._id)));
      setSelected({});
      setStep('complete');

      if (onSuccess) onSuccess(childrenIds);
    } catch (e) {
      console.error('Error transferring children to another kindergarten:', e);
      setSubmitError(e.message || 'Виникла помилка під час переведення дітей.');
      setStep('form');
    }
  };

  const handleFileChange = (newFiles) => {
    setFiles(newFiles);
    if (errors.files) setErrors(prev => ({ ...prev, files: '' }));
  };

  // FileUploader (перегляд/видалення файлу) рендериться всередині <Form>,
  // тож клік по його кнопках без цього спливає і триґерить сабміт форми.
  const handleFormClick = (e) => {
    const target = e.target;
    const isFileUploaderButton = target.closest('.file-uploader-group .btn, .file-uploader-group button, .file-uploader-group .action-link');
    const isFileUploaderCardAction = target.closest('.uploaded-file-card .file-actions button');

    if (isFileUploaderButton || isFileUploaderCardAction) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleFormKeyDown = (e) => {
    if (e.key === 'Enter') {
      const activeElement = document.activeElement;
      const isFileUploaderButton = activeElement.closest('.file-uploader-group .btn, .file-uploader-group button');

      if (isFileUploaderButton) {
        e.preventDefault();
        e.stopPropagation();
      }
    }
  };

  if (!sourceGroup) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Масове переведення дітей до іншого садочка</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit} onClick={handleFormClick} onKeyDown={handleFormKeyDown}>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {step === 'complete' ? (
            <Alert variant="success" className="mb-0">
              Дітей ({transferredCount}) успішно переведено. Місця у поточній групі звільнено, у кожної дитини додано запис в історії переведень.
            </Alert>
          ) : (
            <>
              <Card className="mb-3">
                <Card.Body className="py-2">
                  <strong>Група-джерело:</strong> №{sourceGroup.number} {sourceGroup.name}
                  {sourceGroup.periodStartYear ? ` (${sourceGroup.periodStartYear}-${Number(sourceGroup.periodStartYear) + 1} н.р.)` : ''}
                </Card.Body>
              </Card>

              {submitError && <Alert variant="danger" className="mb-3">{submitError}</Alert>}

              {loadingChildren ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Завантаження списку дітей...
                </div>
              ) : childrenList.length === 0 ? (
                <Alert variant="info">У цій групі немає дітей для переведення.</Alert>
              ) : (
                <>
                  <p className="mb-1">Оберіть дітей, яких потрібно перевести:</p>
                  <div className="mb-2">
                    <Button variant="link" size="sm" className="p-0 me-3" onClick={selectAll} disabled={step === 'processing'} type="button">
                      Обрати всіх
                    </Button>
                    <Button variant="link" size="sm" className="p-0" onClick={deselectAll} disabled={step === 'processing'} type="button">
                      Зняти всіх
                    </Button>
                  </div>
                  <div style={{ border: '1px solid #dee2e6', borderRadius: 4 }}>
                    <table className="table table-sm mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: 40 }}></th>
                          <th>Прізвище</th>
                          <th>Ім'я</th>
                          <th>Дата народження</th>
                          <th>Тип місця (поточний)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {childrenList.map(ch => (
                          <tr key={ch._id} onClick={() => toggleChild(ch._id)} style={{ cursor: 'pointer' }}>
                            <td onClick={(e) => e.stopPropagation()}>
                              <input type="checkbox" checked={!!selected[ch._id]} onChange={() => toggleChild(ch._id)} />
                            </td>
                            <td>{ch.lastName}</td>
                            <td>{ch.firstName}</td>
                            <td>{formatBirthDate(ch.birthDate)}</td>
                            <td>{getPlaceTypeLabel(ch.placeType)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {errors.children && <div className="text-danger small mt-1">{errors.children}</div>}
                  <p className="text-muted mt-2 mb-0">Обрано дітей: {selectedCount} з {childrenList.length}</p>
                </>
              )}

              <hr className="my-4" />

              <Form.Group className="mb-3">
                <Form.Label>Садочок для переведення <span className="text-danger">*</span></Form.Label>
                <AutocompleteSelect
                  value={targetKindergarten}
                  options={availableKindergartens}
                  onSearch={handleKindergartenSearch}
                  onChange={handleTargetKindergartenChange}
                  placeholder="Оберіть садочок"
                  loading={searchingKindergartens}
                  label=""
                  className="small"
                  fieldsValue={['number', 'name']}
                />
                {errors.targetKindergarten && <div className="invalid-feedback d-block">{errors.targetKindergarten}</div>}
                <Form.Text className="text-muted">Введіть назву або номер садочка для пошуку</Form.Text>
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Група для переведення <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      value={targetGroup}
                      onChange={(e) => { setTargetGroup(e.target.value); if (errors.targetGroup) setErrors(prev => ({ ...prev, targetGroup: '' })); }}
                      disabled={!targetKindergarten || loadingGroups}
                      isInvalid={!!errors.targetGroup}
                    >
                      <option value="">{loadingGroups ? 'Завантаження...' : 'Оберіть групу'}</option>
                      {availableGroups.map(g => (
                        <option key={g._id} value={g._id}>{formatGroupLabel(g)}</option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">{errors.targetGroup}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Тип місця <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      value={placeType}
                      onChange={(e) => { setPlaceType(e.target.value); if (errors.placeType) setErrors(prev => ({ ...prev, placeType: '' })); }}
                      isInvalid={!!errors.placeType}
                    >
                      <option value="">Оберіть тип місця</option>
                      {placeTypeOptions.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">{errors.placeType}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Дата переведення <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="date"
                  value={transferDate}
                  onChange={(e) => { setTransferDate(e.target.value); if (errors.transferDate) setErrors(prev => ({ ...prev, transferDate: '' })); }}
                  min={getCurrentDate()}
                  isInvalid={!!errors.transferDate}
                />
                <Form.Control.Feedback type="invalid">{errors.transferDate}</Form.Control.Feedback>
                <Form.Text className="text-muted">Дата, з якої діти починають відвідувати новий садочок; з цієї ж дати місця у поточній групі звільняються</Form.Text>
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Номер наказу <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      value={orderNumber}
                      onChange={(e) => { setOrderNumber(e.target.value); if (errors.orderNumber) setErrors(prev => ({ ...prev, orderNumber: '' })); }}
                      isInvalid={!!errors.orderNumber}
                    />
                    <Form.Control.Feedback type="invalid">{errors.orderNumber}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Дата наказу <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="date"
                      value={orderDate}
                      onChange={(e) => { setOrderDate(e.target.value); if (errors.orderDate) setErrors(prev => ({ ...prev, orderDate: '' })); }}
                      isInvalid={!!errors.orderDate}
                    />
                    <Form.Control.Feedback type="invalid">{errors.orderDate}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Причина переведення <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={reason}
                  onChange={(e) => { setReason(e.target.value); if (errors.reason) setErrors(prev => ({ ...prev, reason: '' })); }}
                  isInvalid={!!errors.reason}
                />
                <Form.Control.Feedback type="invalid">{errors.reason}</Form.Control.Feedback>
              </Form.Group>

              <FileUploader
                label="Скан-копія наказу на переведення"
                description="Завантажте скан-копію наказу у форматі PDF, JPG, PNG. Максимальний розмір файлу 2 МБ. Один наказ застосується до всіх обраних дітей."
                value={files}
                onChange={handleFileChange}
                required={true}
                disabled={step === 'processing'}
                multiple={false}
                errorVal={errors.files}
              />

              <Alert variant="warning" className="mt-3 mb-0">
                <strong>Увага!</strong> Це постійне переведення:
                <ul className="mb-0 mt-2">
                  <li>Місце в поточній групі буде звільнено одразу</li>
                  <li>Дитину буде додано до обраної групи іншого садочка</li>
                  <li>Кожній дитині буде додано запис в історії статусів (з посиланням на цей наказ)</li>
                </ul>
              </Alert>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          {step === 'complete' ? (
            <Button variant="success" onClick={onHide} type="button">Закрити</Button>
          ) : (
            <>
              <Button variant="secondary" onClick={onHide} disabled={step === 'processing'} type="button">
                Скасувати
              </Button>
              <Button variant="primary" type="submit" disabled={step === 'processing' || loadingChildren}>
                {step === 'processing' ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                    Обробка...
                  </>
                ) : (
                  'Підтвердити переведення'
                )}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default TransferToAnotherKindergartenModal;